import { useMemo } from 'react';

import {
	AnswerReveal,
	PointsRow,
	StandardPatternBody,
} from '@quizzach/components';
import { IQuestion } from '@quizzach/interfaces';
import { abcdAnswerHtml, analyzeAbcdQuestion } from '@quizzach/utils';
import {
	OptList,
	QuestionText,
	QuestionContainer,
} from '@quizzach/play-styles';

interface IAbcdQuestionProps {
	color: string;
	currentQuestionNumber: number;
	currentQuestion: IQuestion;
	totalQuestions?: number;
}

interface IAbcdQuestionAnswerProps {
	questionContainerColor: string;
	answerContainerColor: string;
	currentQuestionNumber: number;
	currentQuestion: IQuestion;
	totalQuestions?: number;
}

type AbcdOption = 'a' | 'b' | 'c' | 'd';

const getAnswerOptions = (question: IQuestion): AbcdOption[] => {
	return question.abcd_question?.abc_question
		? ['a', 'b', 'c']
		: ['a', 'b', 'c', 'd'];
};

const AbcdOptions = ({ question }: { question: IQuestion }) => (
	<OptList>
		{getAnswerOptions(question).map((option) => (
			<div className='opt' key={option}>
				<span className='L'>{option}</span>
				<span
					className='t'
					dangerouslySetInnerHTML={{
						__html: question.abcd_question?.[option] || '',
					}}
				/>
			</div>
		))}
	</OptList>
);

export const AbcdQuestionDesktop = (props: IAbcdQuestionProps) => {
	const { color, currentQuestion } = props;

	const analysis = useMemo(
		() => analyzeAbcdQuestion(currentQuestion),
		[currentQuestion]
	);

	if (!currentQuestion) {
		return null;
	}

	return (
		<>
			{analysis.pattern || analysis.note || analysis.kicker ? (
				<StandardPatternBody analysis={analysis} color={color} />
			) : (
				<QuestionContainer color={color}>
					<QuestionText>
						<div
							dangerouslySetInnerHTML={{
								__html: currentQuestion.abcd_question?.text || '',
							}}
						/>
					</QuestionText>
				</QuestionContainer>
			)}
			<AbcdOptions question={currentQuestion} />
			<PointsRow
				points={currentQuestion.points}
				explanation={currentQuestion.points_explanation}
			/>
		</>
	);
};

export const AbcdQuestionAnswerDesktop = (props: IAbcdQuestionAnswerProps) => {
	const { questionContainerColor, currentQuestionNumber, currentQuestion } =
		props;

	const analysis = useMemo(
		() => analyzeAbcdQuestion(currentQuestion),
		[currentQuestion]
	);

	return (
		<>
			{analysis.pattern || analysis.note || analysis.kicker ? (
				<StandardPatternBody
					analysis={analysis}
					color={questionContainerColor}
					dim
				/>
			) : (
				<QuestionContainer color={questionContainerColor}>
					<QuestionText style={{ margin: '20px 0px 0px' }}>
						<div
							dangerouslySetInnerHTML={{
								__html: currentQuestion.abcd_question?.text || '',
							}}
						/>
					</QuestionText>
				</QuestionContainer>
			)}
			<AbcdOptions question={currentQuestion} />
			<AnswerReveal
				revealKey={currentQuestionNumber}
				answerHtml={abcdAnswerHtml(
					currentQuestion.abcd_question?.answer || '',
					currentQuestion.abcd_question
				)}
				explanation={currentQuestion.abcd_question?.explanation}
			/>
			<PointsRow
				points={currentQuestion.points}
				explanation={currentQuestion.points_explanation}
			/>
		</>
	);
};
